import { Colors } from "@/enums/Colors";
import { DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import { Box, List, ListItem, SxProps, Typography, listItemClasses, styled } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonButton from "../utils/CommonButton";
import { ROOMS_FILE_SIZE_UPLOAD_LIMIT } from "../utils/Constants";
import LazoModal from "../utils/LazoModal";
import DataRoomFileDragDrop from "./DataRoomFileDragDrop";
import DataRoomUploadCompleteMessage from "./DataRoomUploadCompleteMessage";
import FileUploadCircleState from "./icons/FileUploadCircleAnimation";
import { FileProgressMap } from "./useFileUploadProgress";

type Props = {
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
  uploadedFiles: File[];
  uploadFunc: (files: File[]) => void;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  fileProgressMap: FileProgressMap;
  resetFileProgressMap: () => void;
  confirmText?: string;
  hideSuccessMessage?: boolean;
  disableMultiple?: boolean;
  disableImportDocCenter?: boolean;
  onClose?: () => void;
};

function DataRoomFileUploadModal(props: Props) {
  const { t } = useTranslation("virtualDataRooms");
  const [openUploadConfirmModal, setOpenUploadConfirmModal] = useState<boolean>(false);

  function handleUploadConfirModalClose() {
    props.setFiles([]);
    props.resetFileProgressMap();
    setOpenUploadConfirmModal(false);
  }

  function handleUploadConfirm() {
    props.resetFileProgressMap();
    props.uploadFunc(props.files);
    setOpenUploadConfirmModal(false);
    props.setOpen(true);
  }

  function onFileDragDrop(files: File[]) {
    files = files.filter((f) => f.size <= ROOMS_FILE_SIZE_UPLOAD_LIMIT);
    const filtered = files.filter((f) => props.uploadedFiles.findIndex((uf) => uf.name === f.name) === -1);
    props.setFiles(filtered);
    if (filtered.length > 0) {
      setOpenUploadConfirmModal(true);
    }
  }

  return (
    <>
      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openUploadConfirmModal}
        title={t("fileUpload.modal.confirmUpload.title")}
        handleClose={handleUploadConfirModalClose}
        buttons={[
          <CommonButton
            key="cancel-file-uploading-button"
            style={{ borderRadius: "0.5rem" }}
            purpleBorderVariant
            onClick={handleUploadConfirModalClose}
          >
            {t("common:cancel")}
          </CommonButton>,
          <CommonButton key="upload-file-button" style={{ borderRadius: "0.5rem" }} onClick={handleUploadConfirm}>
            {t("fileUpload.modal.confirmUpload.buttons.upload")}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          {props.confirmText ? props.confirmText : t("fileUpload.modal.confirmUpload.defaultMessage", { count: props.files.length })}
        </Typography>
      </LazoModal>
      <LazoModal
        sx={FileUploadLazoModalSx}
        open={props.open}
        title={t("fileUpload.modal.uploadFiles.title")}
        handleClose={() => {
          props.setOpen(false);
          props.onClose?.();
        }}
      >
        <DataRoomFileDragDrop
          disableImportDocCenter={props.disableImportDocCenter}
          disableMultiple={props.disableMultiple}
          hideSubtitle
          onFileUpload={onFileDragDrop}
        />
        {!props.hideSuccessMessage && props.uploadedFiles.length !== 0 && props.files.length === 0 && <DataRoomUploadCompleteMessage />}
        <FileUploadList>
          {props.uploadedFiles.map((f) => (
            <ListItem key={f.name}>
              <Box className="filename-container">
                <FileUploadCircleState progress={1} />
                <Typography className="filename">{f.name}</Typography>
              </Box>
              <Box className="progress-container">
                <FillProgress progress={1} />
                <EmptyProgress remaining={0} />
              </Box>
            </ListItem>
          ))}
          {props.files.map((f) => {
            const prog = props.fileProgressMap[f.name] / f.size;
            return (
              <ListItem key={f.name}>
                <Box className="filename-container">
                  <FileUploadCircleState progress={prog || 0} />
                  <Typography className="filename">{f.name}</Typography>
                </Box>
                <Box className="progress-container">
                  <FillProgress progress={prog} />
                  <EmptyProgress remaining={1 - (prog || 0)} />
                </Box>
              </ListItem>
            );
          })}
        </FileUploadList>
      </LazoModal>
    </>
  );
}

const FileUploadList = styled(List)(() => ({
  display: "flex",
  flexDirection: "column",
  maxHeight: "18.75rem", // 300px
  overflowX: "hidden",
  overflowY: "auto",
  alignItems: "center",
  width: "100%",
  [`& .${listItemClasses.root}`]: {
    display: "flex",
    flexDirection: "row",
    minHeight: "3rem",
    height: "3rem",
    alignItems: "center",
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    "&:last-of-type": { border: 0 },
    justifyContent: "space-between",
    width: "100%",
    "& .filename-container": {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      maxWidth: "60%",
      "& .filename": {
        overflowX: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "0.75rem",
        fontWeight: "400",
        lineHeight: "18px",
      },
    },
    "& .progress-container": {
      display: "flex",
      alignItems: "center",
    },
  },
}));

const FileUploadLazoModalSx: SxProps = {
  "& .title-container": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
  },
  "&.modal-container": {
    maxWidth: "41.938rem",
    width: "41.938rem",
    gap: "1.5rem",
  },
};

const FillProgress = styled(Box, {
  shouldForwardProp: (prop) => prop !== "progress",
})<{ progress: number }>(({ progress }) => ({
  height: "6px",
  width: progress * 200 + "px",
  backgroundColor: Colors.BLUE_4763F4,
  borderRadius: "6px 0 0 6px",
  ...(progress >= 0.99 && {
    borderRadius: "6px",
    backgroundColor: Colors.GREEN_01A902,
  }),
}));

const EmptyProgress = styled(Box, {
  shouldForwardProp: (prop) => prop !== "remaining",
})<{ remaining: number }>(({ remaining }) => ({
  height: "6px",
  width: remaining * 200 + "px",
  backgroundColor: Colors.BUTTON_GRAY,
  borderRadius: "0 6px 6px 0",
  ...(remaining === 1 && {
    borderRadius: "6px",
  }),
}));

export default DataRoomFileUploadModal;
